// ABOUTME: Relation-portal modal for creating a new support ticket. Mirrors the warehouse modal
// ABOUTME: but drops the relation selector — the relation is implicit via the auth header.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import { themeSettings, tokens } from "../../../../../theme";
import SHModal from "../../../../../components/theme/Modal/SHModal";
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import ShValidatedInput from "../../../../../components/theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../../../../components/theme/dropdowns/ShValidatedInputDropdown";
import { DropdownBaseV2 } from "../../../../../components/theme/dropdowns/Dropdown";
import ChatInput from "../../../../warehouse/relation/service/components/chat/ChatInput";
import { useTranslation } from "react-i18next";
import {
    useCreateSupportTicketMutation,
    useGetSupportTicketLabelsQuery,
} from "../../../../../newapi/warehouse/supportTicketApiSlice";
import { showToastMessagePostRequest } from "../../../../../utils/toasts";
import { buildSupportTicketSchema } from "../../../../../utils/supportTicketSchema";
import { search_keys } from "../../../../../utils/searchKeys";
import {
    support_ticket_linked_type_to_search_key,
    support_ticket_type,
} from "../../../../../utils/staticEnums";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import { getContrastText } from "../../../../global/branding/brandConfig";
import { hexToRgb } from "../../../../../utils/colorConverters";
import MAddSupportTicketLinkedItem from "../../../../../components/global/Modals/MAddSupportTicketLinkedItem";

const PRIORITY_OPTIONS = [
    { id: 'low', name: 'low' },
    { id: 'medium', name: 'medium' },
    { id: 'high', name: 'high' },
    { id: 'urgent', name: 'urgent' },
];

const TICKET_TYPE_OPTIONS = Object.values(support_ticket_type).map((v) => ({
    id: v,
    name: v,
}));

const TicketTypeOption = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const option = props.option ?? {};
    const value = option.id ?? "";
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 1 }}>
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: generateColorFromTitle(value, theme.palette.mode, 90),
                    border: `1px solid ${generateIconColorFromTitle(value, theme.palette.mode)}`,
                    flexShrink: 0,
                }}
            />
            <Text semibold>{t(value)}</Text>
        </Box>
    );
};

const LabelOption = (props) => {
    const option = props.option ?? {};
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 1 }}>
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: option.color,
                    flexShrink: 0,
                }}
            />
            <Text semibold>{option.shortName}</Text>
            {option.longName && (
                <Text light noBreak sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {option.longName}
                </Text>
            )}
        </Box>
    );
};

const MAddSupportTicket = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const descriptionDataRef = useRef({ content: null, attachments: null });
    const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();
    const { data: labelsData } = useGetSupportTicketLabelsQuery();

    const [ticketInfo, setTicketInfo] = useState({
        title: "",
        priority: "medium",
    });

    const [fieldErrors, setFieldErrors] = useState({});

    const [selectedLinkedItems, setSelectedLinkedItems] = useState(props.initialLinkedItems || []);
    const [linkOpen, setLinkOpen] = useState(false);

    // Opened from a sidepanel action while already mounted: start with that object linked.
    useEffect(() => {
        if (props.open && props.initialLinkedItems?.length) {
            setSelectedLinkedItems(props.initialLinkedItems);
        }
    }, [props.open, props.initialLinkedItems]);

    const handleSelectLinkedItem = (item) => {
        setSelectedLinkedItems((prev) => [...prev, item]);
    };

    const handleRemoveLinkedItem = (idx) => {
        setSelectedLinkedItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const schema = buildSupportTicketSchema(t);

    const handleChange = (key, value) => {
        setTicketInfo({ ...ticketInfo, [key]: value });
        setFieldErrors({ ...fieldErrors, [key]: '' });
    };

    const handleSave = async () => {
        if (isLoading) return;
        try {
            setFieldErrors({});
            await schema.validate(ticketInfo, { abortEarly: false });

            const { content, attachments } = descriptionDataRef.current;

            const formData = new FormData();
            formData.append('title', ticketInfo.title);
            formData.append('priority', ticketInfo.priority);
            formData.append('ticketType', ticketInfo.ticketType);

            if (content) {
                formData.append('description', typeof content === 'string' ? content : JSON.stringify(content));
            }

            if (selectedLinkedItems.length > 0) {
                formData.append('linkedItems', JSON.stringify(selectedLinkedItems));
            }

            if (attachments) {
                attachments.forEach(att => formData.append('files', att.file));
            }

            const data = await createSupportTicket(formData).unwrap();
            showToastMessagePostRequest(t, data);

            setTicketInfo({ title: "", priority: "medium" });
            setSelectedLinkedItems([]);
            descriptionDataRef.current = { content: null, attachments: null };
            props.handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessagePostRequest(t, { success: false, error: err.error });
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return (
        <>
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            sx={{ body: { maxWidth: 720,minWidth:720 } }}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>{t("add_support_ticket")}</Subheading>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                {/* Ticket type */}
                <Box
                    sx={{
                        width: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 7
                    }}>
                    <Box
                        sx={{
                            width: 120,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0
                        }}
                    >
                        <Text light>{t("type")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        <DropdownBaseV2
                            displayName={"name"}
                            options={TICKET_TYPE_OPTIONS}
                            selected={TICKET_TYPE_OPTIONS.findIndex(
                                (o) => o.id === ticketInfo.ticketType
                            )}
                            onChange={(idx) => {
                                if (idx === -1) return;
                                handleChange("ticketType", TICKET_TYPE_OPTIONS[idx].id);
                            }}
                            shComponent={<TicketTypeOption />}
                            defaultStyling
                            closeOnSelection
                            disallowDeselection
                            stopPropagation
                            styles={{
                                optionSx: {
                                    paddingX: 1,
                                    pl: 0.5,
                                    py: 0,
                                    borderRadius: 3,
                                    height: 28,
                                },
                            }}
                        >
                            {({ onClick }) => {
                                const value = ticketInfo.ticketType;
                                return (
                                    <ButtonBase
                                        onClick={onClick}
                                        sx={{
                                            width: 1,
                                            height: 32,
                                            borderRadius: 4,
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? colors.grey[300]
                                                    : colors.grey[100],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            gap: 1,
                                            px: 1.5,
                                            ...(fieldErrors.ticketType ? {
                                                background: colors.red[100],
                                                border: `1px solid ${colors.red[400]}`,
                                            } : {}),
                                        }}
                                    >
                                        {value ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: 2,
                                                        background: generateColorFromTitle(
                                                            value,
                                                            theme.palette.mode,
                                                            90
                                                        ),
                                                        border: `1px solid ${generateIconColorFromTitle(value, theme.palette.mode)}`,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text semibold>{t(value)}</Text>
                                            </>
                                        ) : (
                                            <Text light>{t("choose_type")}</Text>
                                        )}
                                    </ButtonBase>
                                );
                            }}
                        </DropdownBaseV2>
                        {fieldErrors.ticketType &&
                            <Box sx={{ pl: 2 }}>
                                <Text bold variant="red">{fieldErrors.ticketType}</Text>
                            </Box>
                        }
                    </Box>
                </Box>

                {/* Priority */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("priority")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInputDropdown
                            name="priority"
                            displayName="name"
                            valueKey="id"
                            options={PRIORITY_OPTIONS}
                            selected={ticketInfo.priority}
                            onChange={handleChange}
                            useTranslation
                        />
                    </Box>
                </Box>

                {/* Title */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("title")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInput
                            name="title"
                            value={ticketInfo.title}
                            onChange={handleChange}
                            error={fieldErrors.title}
                        />
                    </Box>
                </Box>

                {/* Description - rich text */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("description")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            p: 2,
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            pb:1,
                            px:1,
                        }}
                    >
                        <ChatInput
                            onDataChange={(data) => { descriptionDataRef.current = data; }}
                            hideSend
                            colors={colors}
                            theme={theme}
                            placeholder={t("description")}
                            viewer="client"
                        />
                    </Box>
                </Box>


                {/* Linked items */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, minHeight: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("linked_items")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, minHeight: 32 }}>
                        {selectedLinkedItems.map((linked, idx) => {
                            const bg = generateColorFromTitle(linked.linkedType, theme.palette.mode, 90);
                            const fg = generateIconColorFromTitle(linked.linkedType, theme.palette.mode);
                            const searchKey = support_ticket_linked_type_to_search_key[linked.linkedType];
                            const icon =
                                linked.linkedType === "webshopcredentials"
                                    ? <StoreOutlinedIcon sx={{ fontSize: 14 }} />
                                    : search_keys[searchKey]?.icon;
                            return (
                                <Box
                                    key={"sli" + idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        py: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 32,
                                            flex: "0 0 auto",
                                            px: 1.5,
                                            pl: 0.5,
                                            borderRadius: 4,
                                            maxWidth: 150,
                                            minWidth: 150,
                                            background: bg,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                borderRadius: 4,
                                                height: 24,
                                                width: 24,
                                                flexShrink: 0,
                                                background: fg,
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& svg": { fontSize: 14 },
                                                "& svg path": { fill: "#fff" },
                                            }}
                                        >
                                            {icon}
                                        </Box>
                                        <Text semibold sx={{ display: "block" }} noBreak>
                                            {linked.displayReference || linked.linkedId}
                                        </Text>
                                    </Box>
                                    <Text sx={{ display: "block", flex: 1 }} noBreak>
                                        {linked.displayName}
                                    </Text>
                                    <ButtonBase
                                        onClick={() => handleRemoveLinkedItem(idx)}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 3,
                                            flexShrink: 0,
                                            background: themeSettings(theme.palette.mode).palette.red.main,
                                            color: themeSettings(theme.palette.mode).palette.red.contrastText,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </ButtonBase>
                                </Box>
                            );
                        })}
                        <Box>
                            <ButtonBase
                                onClick={() => setLinkOpen(true)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 3,
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[400]
                                            : colors.grey[100],
                                    color: colors.txt["primary"],
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AddIcon sx={{ fontSize: 16 }} />
                            </ButtonBase>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    },
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={isLoading}
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                variant="contained"
                        onClick={handleSave}
                    >
                        {t("create")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>

        <MAddSupportTicketLinkedItem
            open={linkOpen}
            handleClose={() => setLinkOpen(false)}
            onSelect={handleSelectLinkedItem}
            linkedItems={selectedLinkedItems}
        />
        </>
    );
};

export default MAddSupportTicket;
